/**
 * @fileoverview Component for displaying ordered sequence of processes
 * Shows the current sequence with drag & drop reordering capabilities
 */

import React from 'react';
import { ListGroup, ListGroupItem, Badge, Button } from 'reactstrap';
import { CentroCostoPeso } from '../types/collections/CentroCostoPeso';
import { ProcesoItem } from './ProcesoItem';

/**
 * Props for ProcesoOrderList component
 */
export interface ProcesoOrderListProps {
  /** List of cost centers in current sequence */
  secuencia: CentroCostoPeso[];
  /** Callback when item is removed from sequence */
  onRemove: (index: number) => void;
  /** Callback when items are reordered */
  onReorder: (fromIndex: number, toIndex: number) => void;
  /** Callback when new item is dropped to add to sequence */
  onAddToSequence: (centroCostoId: string) => void;
  /** Callback when weight of an item changes */
  onWeightChange?: (index: number, peso: number) => void;
  /** Whether the component is in read-only mode */
  readOnly?: boolean;
}

/**
 * Component for displaying ordered sequence of processes
 * Each item can be reordered or removed
 */
export const ProcesoOrderList: React.FC<ProcesoOrderListProps> = ({
  secuencia,
  onRemove,
  onReorder,
  onAddToSequence,
  onWeightChange,
  readOnly = false
}) => {
  /**
   * Handle drop of a cost center from available list
   * @param e - Drop event
   */
  const handleDrop = (e: React.DragEvent) => {
    if (readOnly) return;
    
    e.preventDefault();
    const centroCostoId = e.dataTransfer.getData('text/plain');
    
    if (centroCostoId) {
      onAddToSequence(centroCostoId);
    }
  };

  /**
   * Handle drag over to allow dropping
   * @param e - Drag over event
   */
  const handleDragOver = (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  /**
   * Handle drag leave
   * @param e - Drag leave event
   */
  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('drag-over');
  };

  /**
   * Handle drag enter
   * @param e - Drag enter event
   */
  const handleDragEnter = (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  if (secuencia.length === 0) {
    return (
      <div 
        className="text-center text-muted py-4 py-md-5 drop-zone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDragEnter={handleDragEnter}
        style={{
          border: '2px dashed #dee2e6',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa',
          minHeight: '120px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <i className="fas fa-arrow-left fa-2x mb-2 text-muted"></i>
        <p className="mb-1 fw-medium">Arrastra centros de costo aquí</p>
        <small className="text-muted">para crear la secuencia de procesos</small>
        
        {!readOnly && (
          <div className="mt-3">
            <small className="text-info d-none d-md-block">
              <i className="fas fa-info-circle me-1"></i>
              Zona de destino para drag & drop
            </small>
            <small className="text-info d-block d-md-none">
              <i className="fas fa-hand-pointer me-1"></i>
              Toca para agregar
            </small>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="proceso-order-list">
      <ListGroup>
        {secuencia.map((centro, index) => (
          <ProcesoItem
            key={`${centro.id}-${index}`}
            centro={centro}
            index={index}
            onRemove={onRemove}
            onReorder={onReorder}
            onWeightChange={onWeightChange}
            readOnly={readOnly}
            isLast={index === secuencia.length - 1}
          />
        ))}
      </ListGroup>

      {/* Drop zone at the end for adding new items */}
      {!readOnly && (
        <div 
          className="mt-3 p-3 border-2 border-dashed border-secondary rounded text-center drop-zone"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDragEnter={handleDragEnter}
          style={{
            backgroundColor: '#f8f9fa',
            transition: 'all 0.2s ease'
          }}
        >
          <small className="text-muted d-flex align-items-center justify-content-center">
            <i className="fas fa-plus me-2"></i>
            <span className="d-none d-md-inline">Arrastra aquí para agregar al final</span>
            <span className="d-inline d-md-none">Toca para agregar</span>
          </small>
        </div>
      )}

      {/* Summary */}
      <div className="mt-3 text-center">
        <small className="text-muted d-block mb-2">
          {secuencia.length} proceso(s) en secuencia
        </small>
        
        {secuencia.length > 0 && (
          <div className="d-flex flex-wrap justify-content-center gap-2">
            <Badge color="success" className="d-flex align-items-center">
              <i className="fas fa-check me-1"></i>
              <span className="d-none d-sm-inline">Secuencia válida</span>
              <span className="d-inline d-sm-none">Válida</span>
            </Badge>
            
            {secuencia.length === 1 && (
              <Badge color="warning" className="d-flex align-items-center">
                <i className="fas fa-exclamation-triangle me-1"></i>
                <span className="d-none d-sm-inline">Mínimo requerido</span>
                <span className="d-inline d-sm-none">Mínimo</span>
              </Badge>
            )}
            
            {(() => {
              const totalPeso = secuencia.reduce((sum, item) => sum + (item.peso || 0), 0);
              return totalPeso > 0 && (
                <Badge 
                  color={totalPeso > 100 ? "danger" : "info"} 
                  className="d-flex align-items-center"
                >
                  <i className="fas fa-weight me-1"></i>
                  {totalPeso}%
                </Badge>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};
